import { useState, useEffect, useCallback } from "react";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";

const STATUS_STYLES = {
  completed: "bg-green-100 text-green-700 border border-green-200",
  pending:   "bg-yellow-100 text-yellow-700 border border-yellow-200",
  failed:    "bg-red-100 text-red-700 border border-red-200",
};

const PLAN_STYLES = {
  starter:    "bg-blue-50 text-blue-600",
  growth:     "bg-purple-50 text-purple-600",
  enterprise: "bg-orange-50 text-orange-600",
};

export default function SuperAdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatus] = useState("all");
  const [toast, setToast]       = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Wrap fetchPayments with useCallback:
    const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
        const res = await api.get("/super-admin/payments");
        setPayments(res.data.payments || []);
        setSummary(res.data.summary || null);
    } catch {
        showToast("Failed to load payments", "error");
    } finally {
        setLoading(false);
    }
    }, []);

// useEffect now has the correct dependency:
    useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const filtered = payments.filter((p) => {
    const matchStatus = statusFilter === "all" || p.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      p.shop_name?.toLowerCase().includes(q) ||
      p.mpesa_ref?.toLowerCase().includes(q) ||
      p.phone?.includes(q);
    return matchStatus && matchSearch;
  });

  const formatDate = (iso) => iso
    ? new Date(iso).toLocaleString("en-KE", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900"
              style={{ fontFamily: "'Sora', sans-serif" }}>
              Payments
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              All M-Pesa transactions across TumaTrack
            </p>
          </div>
          <button
            onClick={fetchPayments}
            className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              {
                label: "Total Revenue",
                value: `KES ${Number(summary.total_revenue).toLocaleString()}`,
                icon: "💰",
                color: "bg-green-50 text-green-600",
              },
              {
                label: "Successful",
                value: summary.total_payments,
                icon: "✅",
                color: "bg-blue-50 text-blue-600",
              },
              {
                label: "Pending",
                value: summary.pending_payments,
                icon: "⏳",
                color: "bg-yellow-50 text-yellow-600",
              },
              {
                label: "Failed",
                value: summary.failed_payments,
                icon: "❌",
                color: "bg-red-50 text-red-600",
              },
            ].map((card) => (
              <div key={card.label}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg ${card.color}`}>
                    {card.icon}
                  </span>
                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">
                    {card.label}
                  </p>
                </div>
                <p className="font-bold text-gray-900 text-xl"
                  style={{ fontFamily: "'Sora', sans-serif" }}>
                  {card.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0z"/>
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by shop, M-Pesa ref, or phone..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-gray-100 rounded-xl p-1">
            {["all", "completed", "pending", "failed"].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-all ${
                  statusFilter === s
                    ? "bg-orange-500 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10"
                  stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Loading payments...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <p className="text-4xl mb-3">💳</p>
              <p className="font-medium text-gray-600">No payments found</p>
              <p className="text-sm mt-1">
                {search || statusFilter !== "all"
                  ? "Try adjusting your search or filter."
                  : "Payments will appear here once shops start subscribing."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    {["Shop", "Phone", "Amount", "Plan", "M-Pesa Ref", "Status", "Date"].map((h) => (
                      <th key={h}
                        className="text-left px-5 py-3.5 text-gray-500 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((payment) => (
                    <tr key={payment.id}
                      className="hover:bg-gray-50/50 transition-colors">

                      {/* Shop */}
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">{payment.shop_name}</p>
                        <p className="text-gray-400 text-xs">{payment.shop_phone}</p>
                      </td>

                      {/* Phone */}
                      <td className="px-5 py-4 text-gray-500 text-xs">
                        {payment.phone}
                      </td>

                      {/* Amount */}
                      <td className="px-5 py-4">
                        <p className="font-bold text-gray-900">
                          KES {Number(payment.amount).toLocaleString()}
                        </p>
                      </td>

                      {/* Plan */}
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                          PLAN_STYLES[payment.plan] || "bg-gray-100 text-gray-600"
                        }`}>
                          {payment.plan}
                        </span>
                      </td>

                      {/* M-Pesa Ref */}
                      <td className="px-5 py-4">
                        {payment.mpesa_ref ? (
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                            {payment.mpesa_ref}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                          STATUS_STYLES[payment.status] || "bg-gray-100 text-gray-600"
                        }`}>
                          {payment.status}
                        </span>
                      </td>

                      {/* Date */}
                      <td className="px-5 py-4 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(payment.paid_at || payment.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && filtered.length > 0 && (
          <p className="text-gray-400 text-xs text-right mt-3">
            Showing {filtered.length} of {payments.length} payments
          </p>
        )}
      </div>
    </SuperAdminLayout>
  );
}